import { Capacitor } from '@capacitor/core'
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import { Logger } from '@egw/logger'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { gt } from 'semver'
import { type Config } from '../../config/config'

const log = new Logger('Live Update Plugin')

export const getLocalVersion = async (): Promise<string> => {
  try {
    const response = await fetch(`/version.json?t=${new Date().getTime().toString()}`)
    if (!response.ok) {
      throw new Error('Failed to fetch local version info')
    }
    const result = (await response.json()) as { version: string }
    return result.version
  } catch (error) {
    log.error('Error fetching local version:', error)
    return ''
  }
}

export const getRemoteVersion = async (domain: string): Promise<string> => {
  try {
    const response = await fetch(`https://${domain}/version.json?t=${new Date().getTime().toString()}`)
    if (!response.ok) {
      throw new Error('Failed to fetch remote version info')
    }
    const result = (await response.json()) as { version: string }
    return result.version
  } catch (error) {
    log.error('Error fetching remote version:', error)
    return ''
  }
}

export const checkForUpdate = async (domain: string): Promise<string | undefined> => {
  try {
    const currentVersion = await getLocalVersion()
    const remoteVersion = await getRemoteVersion(domain)
    return gt(remoteVersion, currentVersion) ? remoteVersion : undefined
  } catch (error) {
    log.error('Error checking for updates:', error)
    return undefined
  }
}

let instance: LiveUpdateService | null = null

class LiveUpdateService {
  private isInitialized = false
  private config: Config | undefined

  constructor() {
    if (!instance) {
      instance = this
    }
  }

  init(config: Config): void {
    if (!this.isInitialized) {
      this.config = config
      this.isInitialized = true
      log.info('Service initialized')
      void this.liveUpdate()
    } else {
      log.warn('Service is already initialized. Skipping initialization...')
    }
  }

  async liveUpdate(): Promise<void> {
    if (Capacitor.isNativePlatform() && this.config?.liveUpdate) {
      const versionToUpdate = await checkForUpdate(this.config.liveUpdate.remoteDomain)
      // alert(`Version to update: ${versionToUpdate}`)
      if (versionToUpdate) {
        log.info('Update available: ', versionToUpdate)
        notifications.dispatch(NOTIFICATIONS.LIVE_UPDATE.START)
        try {
          await LiveUpdate.downloadBundle({
            url: `https://${this.config.liveUpdate.remoteDomain}/v${versionToUpdate}.zip`,
            bundleId: versionToUpdate,
          })
          // alert('Downloaded')
          log.info('Bundle downloaded')
          await LiveUpdate.setNextBundle({
            bundleId: versionToUpdate,
          })
          // alert('Set bundle')
          log.info('Bundle set')
          await LiveUpdate.reload()
          // alert('App reloaded')
          await LiveUpdate.ready()
          // alert('App ready')
          log.info('App reloaded')
        } catch (error) {
          // alert(error)
          log.error('Error downloading bundle:', error)
        } finally {
          notifications.dispatch(NOTIFICATIONS.LIVE_UPDATE.STOP)
        }
      } else {
        log.info('No updates available')
      }
    } else {
      log.info('Live update functionality is not available')
    }
  }
}

export const liveUpdateService = new LiveUpdateService()
